import React, { useState, useEffect, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    Card,
    CardBody,
    CardHeader,
    Row,
    Col,
    Button,
    Container,
    FormGroup,
    Label,
    Input,
    InputGroup,
    InputGroupAddon,
    InputGroupText
} from "reactstrap";
import SignatureCanvas from 'react-signature-canvas';
import NotificationAlert from "react-notification-alert";
import rest from "../../API/api";

const SubmitWaiver = () => {
    const { id } = useParams();
    const [waiver, setWaiver] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const history = useHistory();
    const signatureRef = useRef(null);
    const parentSignatureRef = useRef(null);
    const notificationAlertRef = useRef(null);

    // Field error states
    const [fieldErrors, setFieldErrors] = useState({});

    // Form fields
    const [firstName, setFirstName] = useState("");
    const [middleName, setMiddleName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [dob, setDob] = useState("");
    const [isMinor, setIsMinor] = useState(false);

    // Address
    const [addressLine1, setAddressLine1] = useState("");
    const [addressLine2, setAddressLine2] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [zipCode, setZipCode] = useState("");
    const [country, setCountry] = useState("United States");

    // Emergency contact
    const [emergencyFirstName, setEmergencyFirstName] = useState("");
    const [emergencyLastName, setEmergencyLastName] = useState("");
    const [emergencyPhone, setEmergencyPhone] = useState("");

    // Parent/Guardian (for minors)
    const [parentGuardianFirstName, setParentGuardianFirstName] = useState("");
    const [parentGuardianLastName, setParentGuardianLastName] = useState("");
    const [parentGuardianDOB, setParentGuardianDOB] = useState("");

    // Consent
    const [electronicConsent, setElectronicConsent] = useState(false);
    const [emailOptIn, setEmailOptIn] = useState(false);

    const notify = (type, message) => {
        const options = {
            place: "tc",
            message: <div>{message}</div>,
            type: type,
            icon: "tim-icons icon-bell-55",
            autoDismiss: 5,
        };
        notificationAlertRef.current.notificationAlert(options);
    };

    // Phone number formatting function
    const formatPhoneNumber = (value) => {
        // Remove all non-digit characters
        const phoneNumber = value.replace(/\D/g, '');

        // Format based on length
        if (phoneNumber.length === 0) {
            return '';
        } else if (phoneNumber.length <= 3) {
            return `(${phoneNumber}`;
        } else if (phoneNumber.length <= 6) {
            return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
        } else {
            return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
        }
    };

    useEffect(() => {
        if (id) {
            loadWaiver();
        }
    }, [id]);

    useEffect(() => {
        // Calculate age when DOB changes
        if (dob) {
            const age = calculateAge(dob);
            setIsMinor(age < 18);
        }
    }, [dob]);

    const calculateAge = (birthDate) => {
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }

        return age;
    };

    const loadWaiver = async () => {
        try {
            const response = await fetch(`${rest.waiversubmit}/${id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': rest.apiKey
                }
            });

            if (response.ok) {
                const data = await response.json();
                setWaiver(data);
            } else {
                alert("Waiver not found or not available");
                history.push("/admin/waivers");
            }
            setLoading(false);
        } catch (error) {
            console.error('Error loading waiver:', error);
            alert("Error loading waiver");
            history.push("/admin/waivers");
        }
    };

    const clearSignature = () => {
        signatureRef.current.clear();
    };

    const clearParentSignature = () => {
        if (parentSignatureRef.current) {
            parentSignatureRef.current.clear();
        }
    };

    const validateForm = () => {
        const errors = {};

        // Check required fields
        if (!firstName.trim()) errors.firstName = true;
        if (!lastName.trim()) errors.lastName = true;
        if (!email.trim()) errors.email = true;
        if (!phone.trim()) errors.phone = true;
        if (!dob) errors.dob = true;
        if (!emergencyFirstName.trim()) errors.emergencyFirstName = true;
        if (!emergencyLastName.trim()) errors.emergencyLastName = true;
        if (!emergencyPhone.trim()) errors.emergencyPhone = true;
        if (!electronicConsent) errors.electronicConsent = true;
        if (signatureRef.current && signatureRef.current.isEmpty()) errors.signature = true;

        // Validate parent/guardian fields if minor
        if (isMinor) {
            if (!parentGuardianFirstName.trim()) errors.parentGuardianFirstName = true;
            if (!parentGuardianLastName.trim()) errors.parentGuardianLastName = true;
            if (!parentGuardianDOB) errors.parentGuardianDOB = true;
            if (!parentSignatureRef.current || parentSignatureRef.current.isEmpty()) errors.parentSignature = true;
        }

        // Update error state
        setFieldErrors(errors);

        // If there are errors, show notification
        if (Object.keys(errors).length > 0) {
            notify("warning", "Please fill in all required fields highlighted in red.");
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        try {
            const signatureData = signatureRef.current.toDataURL();
            const parentSignatureData = isMinor && parentSignatureRef.current
                ? parentSignatureRef.current.toDataURL()
                : "";

            const submission = {
                WaiverId: waiver._id,
                WaiverTitle: waiver.Title,
                WaiverVersion: waiver.Version,
                FirstName: firstName,
                MiddleName: middleName,
                LastName: lastName,
                Email: email,
                Phone: phone,
                DOB: dob,
                AddressLine1: addressLine1,
                AddressLine2: addressLine2,
                City: city,
                State: state,
                ZipCode: zipCode,
                Country: country,
                EmergencyContactFirstName: emergencyFirstName,
                EmergencyContactLastName: emergencyLastName,
                EmergencyContactPhone: emergencyPhone,
                ParentGuardianFirstName: parentGuardianFirstName,
                ParentGuardianLastName: parentGuardianLastName,
                ParentGuardianDOB: parentGuardianDOB,
                ParentGuardianSignature: parentSignatureData,
                Signature: signatureData,
                SignatureDate: new Date(),
                ElectronicSignatureConsent: electronicConsent,
                EmailOptIn: emailOptIn
            };

            const response = await fetch(rest.waiversubmit, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': rest.apiKey
                },
                body: JSON.stringify(submission)
            });

            if (response.ok) {
                const result = await response.json();
                setSubmitted(true);
                console.log('Waiver submitted successfully:', result);
            } else {
                alert("Failed to submit waiver. Please try again.");
            }
        } catch (error) {
            console.error('Error submitting waiver:', error);
            alert("Error submitting waiver. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const renderWaiverSection = (header, text) => {
        if (!header && !text) return null;

        return (
            <div className="mb-5">
                {header && (
                    <h4 className="text-center mb-4" style={{
                        fontFamily: 'Georgia, "Times New Roman", serif',
                        fontWeight: '700',
                        fontSize: '1.5rem',
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        color: '#fff',
                        borderBottom: '2px solid #1d8cf8',
                        paddingBottom: '10px',
                        marginBottom: '20px'
                    }}>
                        {header}
                    </h4>
                )}
                {text && (
                    <p style={{
                        fontFamily: 'Georgia, "Times New Roman", serif',
                        whiteSpace: 'pre-wrap',
                        lineHeight: '1.8',
                        textAlign: 'justify',
                        fontSize: '1.05rem',
                        color: '#fff',
                        marginBottom: '2rem',
                        wordSpacing: 'normal',
                        wordWrap: 'break-word',
                        hyphens: 'auto'
                    }}>
                        {text}
                    </p>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="content">
                <Container>
                    <Row className="justify-content-center">
                        <Col md={8}>
                            <Card>
                                <CardBody>
                                    <p className="text-center">Loading waiver...</p>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="content">
                <Container>
                    <Row className="justify-content-center">
                        <Col md={8}>
                            <Card className="card-user">
                                <CardBody className="text-center">
                                    <div className="author">
                                        <div className="block block-one"></div>
                                        <div className="block block-two"></div>
                                        <div className="block block-three"></div>
                                        <div className="block block-four"></div>
                                        <i className="tim-icons icon-check-2" style={{fontSize: '4rem', color: '#1d8cf8'}}></i>
                                        <h3 className="title mt-3">Waiver Submitted Successfully!</h3>
                                    </div>
                                    <p className="description">
                                        Thank you for completing the waiver. Your submission has been recorded.
                                    </p>
                                    <Button color="primary" onClick={() => history.push("/admin/signwaiver")}>
                                        Return to Waivers
                                    </Button>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    return (
        <div className="content">
            <div className="react-notification-alert-container">
                <NotificationAlert ref={notificationAlertRef} />
            </div>
            <style>
                {`
                    .input-group {
                        border: 2px solid #2c3e50 !important;
                        border-radius: 4px !important;
                        background-color: white !important;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
                    }
                    .input-group .form-control {
                        border: none !important;
                        background-color: white !important;
                        color: #000 !important;
                    }
                    .input-group .input-group-text {
                        border: none !important;
                        background-color: white !important;
                        color: #2c3e50 !important;
                    }
                    .input-group .form-control::placeholder {
                        color: #6c757d !important;
                    }
                    .input-group.has-error {
                        border-color: #fd5d93 !important;
                        box-shadow: 0 0 0 0.2rem rgba(253, 93, 147, 0.25) !important;
                    }
                    .signature-error {
                        border-color: #fd5d93 !important;
                        box-shadow: 0 0 0 0.2rem rgba(253, 93, 147, 0.25) !important;
                    }
                `}
            </style>
            <Container>
                {/* Title and Version - Outside Card */}
                <Row className="justify-content-center mb-4">
                    <Col md={10} className="text-center">
                        <h2 style={{ color: '#fff', marginBottom: '10px' }}>{waiver.Title}</h2>
                        {waiver.Version && <p style={{ color: '#aaa', fontSize: '1.1rem' }}>Version {waiver.Version}</p>}
                    </Col>
                </Row>

                {/* Image in White Box */}
                {waiver.ImageUrl && (
                    <Row className="justify-content-center mb-4">
                        <Col md={10}>
                            <div style={{
                                padding: '30px',
                                backgroundColor: 'white',
                                border: '2px solid #2c3e50',
                                borderRadius: '4px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                textAlign: 'center'
                            }}>
                                <img src={waiver.ImageUrl} alt="Waiver Header" style={{maxWidth: '100%', maxHeight: '300px'}} />
                            </div>
                        </Col>
                    </Row>
                )}

                {/* Waiver Content - Headers and Text on Dark Background */}
                <Row className="justify-content-center mb-4">
                    <Col md={10}>
                        {renderWaiverSection(waiver.Header1, waiver.WaiverText1)}
                        {renderWaiverSection(waiver.Header2, waiver.WaiverText2)}
                        {renderWaiverSection(waiver.Header3, waiver.WaiverText3)}
                    </Col>
                </Row>

                <Row className="justify-content-center">
                    <Col md={10}>
                        <Card>
                            <CardBody>

                                {/* Participant Information Section */}
                                <Row className="mb-4">
                                    <Col md={12}>
                                        <div style={{
                                            padding: '30px',
                                            backgroundColor: 'white',
                                            border: '2px solid #2c3e50',
                                            borderRadius: '4px',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}>
                                            <h4 style={{ color: '#000', marginBottom: '25px', borderBottom: '2px solid #2c3e50', paddingBottom: '10px' }}>Participant Information</h4>
                                            <Row>
                                                <Col md={4}>
                                                    <FormGroup>
                                                        <Label style={{ color: '#000' }}>First Name <span className="text-danger">*</span></Label>
                                                        <InputGroup className={fieldErrors.firstName ? 'has-error' : ''}>
                                                <InputGroupAddon addonType="prepend">
                                                    <InputGroupText><i className="tim-icons icon-single-02"/></InputGroupText>
                                                </InputGroupAddon>
                                                <Input
                                                    type="text"
                                                    value={firstName}
                                                    onChange={(e) => setFirstName(e.target.value)}
                                                    required
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label style={{ color: '#000' }}>Middle Name</Label>
                                            <InputGroup>
                                                <InputGroupAddon addonType="prepend">
                                                    <InputGroupText><i className="tim-icons icon-single-02"/></InputGroupText>
                                                </InputGroupAddon>
                                                <Input
                                                    type="text"
                                                    value={middleName}
                                                    onChange={(e) => setMiddleName(e.target.value)}
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label style={{ color: '#000' }}>Last Name <span className="text-danger">*</span></Label>
                                            <InputGroup className={fieldErrors.lastName ? 'has-error' : ''}>
                                                <InputGroupAddon addonType="prepend">
                                                    <InputGroupText><i className="tim-icons icon-single-02"/></InputGroupText>
                                                </InputGroupAddon>
                                                <Input
                                                    type="text"
                                                    value={lastName}
                                                    onChange={(e) => setLastName(e.target.value)}
                                                    required
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label style={{ color: '#000' }}>Email <span className="text-danger">*</span></Label>
                                            <InputGroup className={fieldErrors.email ? 'has-error' : ''}>
                                                <InputGroupAddon addonType="prepend">
                                                    <InputGroupText><i className="tim-icons icon-email-85"/></InputGroupText>
                                                </InputGroupAddon>
                                                <Input
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    required
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label style={{ color: '#000' }}>Phone <span className="text-danger">*</span></Label>
                                            <InputGroup className={fieldErrors.phone ? 'has-error' : ''}>
                                                <InputGroupAddon addonType="prepend">
                                                    <InputGroupText><i className="tim-icons icon-mobile"/></InputGroupText>
                                                </InputGroupAddon>
                                                <Input
                                                    type="tel"
                                                    value={phone}
                                                    onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                                                    placeholder="(555) 555-5555"
                                                    required
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label style={{ color: '#000' }}>Date of Birth <span className="text-danger">*</span></Label>
                                            <InputGroup className={fieldErrors.dob ? 'has-error' : ''}>
                                                <InputGroupAddon addonType="prepend">
                                                    <InputGroupText><i className="tim-icons icon-calendar-60"/></InputGroupText>
                                                </InputGroupAddon>
                                                <Input
                                                    type="date"
                                                    value={dob}
                                                    onChange={(e) => setDob(e.target.value)}
                                                    required
                                                />
                                            </InputGroup>
                                            {dob && isMinor && (
                                                <small className="text-warning">
                                                    <i className="tim-icons icon-alert-circle-exc"/> Minor - Parent/Guardian signature required
                                                </small>
                                            )}
                                        </FormGroup>
                                    </Col>
                                </Row>
                                        </div>
                                    </Col>
                                </Row>

                                {/* Address Section */}
                                <Row className="mb-4">
                                    <Col md={12}>
                                        <div style={{
                                            padding: '30px',
                                            backgroundColor: '#e8f4f8',
                                            border: '2px solid #1565c0',
                                            borderRadius: '4px',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}>
                                            <h4 style={{ color: '#1565c0', marginBottom: '25px', borderBottom: '2px solid #1565c0', paddingBottom: '10px' }}>Address</h4>
                                            <Row>
                                    <Col md={12}>
                                        <FormGroup>
                                            <Label style={{ color: '#0d47a1' }}>Address Line 1</Label>
                                            <InputGroup>
                                                <InputGroupAddon addonType="prepend">
                                                    <InputGroupText><i className="tim-icons icon-square-pin"/></InputGroupText>
                                                </InputGroupAddon>
                                                <Input
                                                    type="text"
                                                    value={addressLine1}
                                                    onChange={(e) => setAddressLine1(e.target.value)}
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={12}>
                                        <FormGroup>
                                            <Label style={{ color: '#0d47a1' }}>Address Line 2</Label>
                                            <InputGroup>
                                                <InputGroupAddon addonType="prepend">
                                                    <InputGroupText><i className="tim-icons icon-square-pin"/></InputGroupText>
                                                </InputGroupAddon>
                                                <Input
                                                    type="text"
                                                    value={addressLine2}
                                                    onChange={(e) => setAddressLine2(e.target.value)}
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label style={{ color: '#0d47a1' }}>City</Label>
                                            <InputGroup>
                                                <InputGroupAddon addonType="prepend">
                                                    <InputGroupText><i className="tim-icons icon-square-pin"/></InputGroupText>
                                                </InputGroupAddon>
                                                <Input
                                                    type="text"
                                                    value={city}
                                                    onChange={(e) => setCity(e.target.value)}
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label style={{ color: '#0d47a1' }}>State</Label>
                                            <InputGroup>
                                                <InputGroupAddon addonType="prepend">
                                                    <InputGroupText><i className="tim-icons icon-square-pin"/></InputGroupText>
                                                </InputGroupAddon>
                                                <Input
                                                    type="text"
                                                    value={state}
                                                    onChange={(e) => setState(e.target.value)}
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label style={{ color: '#0d47a1' }}>Zip Code</Label>
                                            <InputGroup>
                                                <InputGroupAddon addonType="prepend">
                                                    <InputGroupText><i className="tim-icons icon-square-pin"/></InputGroupText>
                                                </InputGroupAddon>
                                                <Input
                                                    type="text"
                                                    value={zipCode}
                                                    onChange={(e) => setZipCode(e.target.value)}
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                        </div>
                                    </Col>
                                </Row>

                                {/* Emergency Contact Section */}
                                <Row className="mb-4">
                                    <Col md={12}>
                                        <div style={{
                                            padding: '30px',
                                            backgroundColor: '#fff9e6',
                                            border: '2px solid #f57c00',
                                            borderRadius: '4px',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}>
                                            <h4 style={{ color: '#e65100', marginBottom: '25px', borderBottom: '2px solid #f57c00', paddingBottom: '10px' }}>Emergency Contact</h4>
                                            <Row>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label style={{ color: '#e65100' }}>First Name <span className="text-danger">*</span></Label>
                                            <InputGroup className={fieldErrors.emergencyFirstName ? 'has-error' : ''}>
                                                <InputGroupAddon addonType="prepend">
                                                    <InputGroupText><i className="tim-icons icon-badge"/></InputGroupText>
                                                </InputGroupAddon>
                                                <Input
                                                    type="text"
                                                    value={emergencyFirstName}
                                                    onChange={(e) => setEmergencyFirstName(e.target.value)}
                                                    required
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label style={{ color: '#e65100' }}>Last Name <span className="text-danger">*</span></Label>
                                            <InputGroup className={fieldErrors.emergencyLastName ? 'has-error' : ''}>
                                                <InputGroupAddon addonType="prepend">
                                                    <InputGroupText><i className="tim-icons icon-badge"/></InputGroupText>
                                                </InputGroupAddon>
                                                <Input
                                                    type="text"
                                                    value={emergencyLastName}
                                                    onChange={(e) => setEmergencyLastName(e.target.value)}
                                                    required
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label style={{ color: '#e65100' }}>Phone <span className="text-danger">*</span></Label>
                                            <InputGroup className={fieldErrors.emergencyPhone ? 'has-error' : ''}>
                                                <InputGroupAddon addonType="prepend">
                                                    <InputGroupText><i className="tim-icons icon-mobile"/></InputGroupText>
                                                </InputGroupAddon>
                                                <Input
                                                    type="tel"
                                                    value={emergencyPhone}
                                                    onChange={(e) => setEmergencyPhone(formatPhoneNumber(e.target.value))}
                                                    placeholder="(555) 555-5555"
                                                    required
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                        </div>
                                    </Col>
                                </Row>

                                {/* Parent/Guardian Section for Minors */}
                                {isMinor && (
                                    <Row className="mb-4">
                                        <Col md={12}>
                                            <div style={{
                                                padding: '30px',
                                                backgroundColor: '#f3e5f5',
                                                border: '2px solid #7b1fa2',
                                                borderRadius: '4px',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                            }}>
                                                <h4 style={{ color: '#6a1b9a', marginBottom: '25px', borderBottom: '2px solid #7b1fa2', paddingBottom: '10px' }}>
                                                    <i className="tim-icons icon-alert-circle-exc"/> Parent/Guardian Information (Required for Minors)
                                                </h4>
                                        <Row>
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label style={{ color: '#6a1b9a' }}>First Name <span className="text-danger">*</span></Label>
                                                    <InputGroup className={fieldErrors.parentGuardianFirstName ? 'has-error' : ''}>
                                                        <InputGroupAddon addonType="prepend">
                                                            <InputGroupText><i className="tim-icons icon-single-02"/></InputGroupText>
                                                        </InputGroupAddon>
                                                        <Input
                                                            type="text"
                                                            value={parentGuardianFirstName}
                                                            onChange={(e) => setParentGuardianFirstName(e.target.value)}
                                                            required
                                                        />
                                                    </InputGroup>
                                                </FormGroup>
                                            </Col>
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label style={{ color: '#6a1b9a' }}>Last Name <span className="text-danger">*</span></Label>
                                                    <InputGroup className={fieldErrors.parentGuardianLastName ? 'has-error' : ''}>
                                                        <InputGroupAddon addonType="prepend">
                                                            <InputGroupText><i className="tim-icons icon-single-02"/></InputGroupText>
                                                        </InputGroupAddon>
                                                        <Input
                                                            type="text"
                                                            value={parentGuardianLastName}
                                                            onChange={(e) => setParentGuardianLastName(e.target.value)}
                                                            required
                                                        />
                                                    </InputGroup>
                                                </FormGroup>
                                            </Col>
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label style={{ color: '#6a1b9a' }}>Date of Birth <span className="text-danger">*</span></Label>
                                                    <InputGroup className={fieldErrors.parentGuardianDOB ? 'has-error' : ''}>
                                                        <InputGroupAddon addonType="prepend">
                                                            <InputGroupText><i className="tim-icons icon-calendar-60"/></InputGroupText>
                                                        </InputGroupAddon>
                                                        <Input
                                                            type="date"
                                                            value={parentGuardianDOB}
                                                            onChange={(e) => setParentGuardianDOB(e.target.value)}
                                                            required
                                                        />
                                                    </InputGroup>
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                        <h5 className="title mt-4" style={{ color: '#6a1b9a' }}>Parent / Guardian Acknowledgment (Required for Minors)</h5>
                                        <p style={{ color: '#6a1b9a', fontSize: '0.95rem', marginBottom: '15px', lineHeight: '1.6' }}>
                                            I am the legal parent/guardian of the minor participant. I acknowledge that I am waiving and releasing the minor's rights and my own rights to the fullest extent permitted by Colorado law. I agree to all terms of this Agreement on behalf of the minor.
                                        </p>
                                        <h6 style={{ color: '#6a1b9a' }}>Parent/Guardian Signature <span className="text-danger">*</span></h6>
                                        <Row>
                                            <Col md={12}>
                                                <div className={fieldErrors.parentSignature ? 'signature-error' : ''} style={{
                                                    border: '2px solid #7b1fa2',
                                                    borderRadius: '5px',
                                                    backgroundColor: 'white',
                                                    marginBottom: '10px'
                                                }}>
                                                    <SignatureCanvas
                                                        ref={parentSignatureRef}
                                                        canvasProps={{
                                                            width: 600,
                                                            height: 200,
                                                            className: 'signature-canvas',
                                                            style: { width: '100%', height: '200px' }
                                                        }}
                                                        backgroundColor="white"
                                                    />
                                                </div>
                                                <Button
                                                    size="sm"
                                                    onClick={clearParentSignature}
                                                    style={{
                                                        backgroundColor: '#7b1fa2',
                                                        borderColor: '#7b1fa2',
                                                        color: 'white'
                                                    }}
                                                >
                                                    Clear Parent/Guardian Signature
                                                </Button>
                                            </Col>
                                        </Row>
                                            </div>
                                        </Col>
                                    </Row>
                                )}

                                <h5 className="title mt-4">Participant (Age 18 or older)</h5>
                                <p style={{ color: '#fff', fontSize: '0.95rem', marginBottom: '15px', lineHeight: '1.6' }}>
                                    I have read and understand this Agreement. I sign voluntarily.
                                </p>
                                <h6 style={{ color: '#fff' }}>Participant Signature <span className="text-danger">*</span></h6>
                                <Row>
                                    <Col md={12}>
                                        <div className={fieldErrors.signature ? 'signature-error' : ''} style={{
                                            border: '2px solid #2b3553',
                                            borderRadius: '5px',
                                            backgroundColor: 'white',
                                            marginBottom: '10px'
                                        }}>
                                            <SignatureCanvas
                                                ref={signatureRef}
                                                canvasProps={{
                                                    width: 600,
                                                    height: 200,
                                                    className: 'signature-canvas',
                                                    style: { width: '100%', height: '200px' }
                                                }}
                                                backgroundColor="white"
                                            />
                                        </div>
                                        <Button color="warning" size="sm" onClick={clearSignature}>
                                            Clear Signature
                                        </Button>
                                    </Col>
                                </Row>

                                <Row className="mt-5">
                                    <Col md={12}>
                                        <div style={{
                                            padding: '20px',
                                            backgroundColor: 'rgba(29, 140, 248, 0.1)',
                                            border: '2px solid #1d8cf8',
                                            borderRadius: '4px',
                                            marginBottom: '20px'
                                        }}>
                                            <h5 style={{ color: '#1d8cf8', marginBottom: '10px' }}>Colorado Inherent Risk Acknowledgment</h5>
                                            <p style={{ color: '#fff', fontSize: '0.95rem', marginBottom: '0', lineHeight: '1.6' }}>
                                                I acknowledge that under Colorado law, participants in recreational and athletic activities assume the inherent risks of such activities. This Agreement is intended to be interpreted and enforced consistent with that principle and Colorado public policy.
                                            </p>
                                        </div>
                                    </Col>
                                </Row>

                                <Row className="mt-4">
                                    <Col md={12}>
                                        <div style={{
                                            display: 'inline-block',
                                            margin: '-2px',
                                            padding: '8px 0',
                                            borderRadius: '4px',
                                            border: fieldErrors.electronicConsent ? '2px solid #ec250d' : '2px solid transparent',
                                            backgroundColor: fieldErrors.electronicConsent ? 'rgba(236, 37, 13, 0.1)' : 'transparent'
                                        }}>
                                            <FormGroup check style={{ marginBottom: 0 }}>
                                                <Label check style={{ color: '#fff' }}>
                                                    <Input
                                                        type="checkbox"
                                                        checked={electronicConsent}
                                                        onChange={(e) => setElectronicConsent(e.target.checked)}
                                                        required
                                                    />
                                                    <span className="form-check-sign"/>
                                                    I consent to the use of electronic signatures and agree that my electronic signature is the legal equivalent of my manual signature <span className="text-danger">*</span>
                                                </Label>
                                            </FormGroup>
                                        </div>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={12}>
                                        <FormGroup check>
                                            <Label check style={{ color: '#fff' }}>
                                                <Input
                                                    type="checkbox"
                                                    checked={emailOptIn}
                                                    onChange={(e) => setEmailOptIn(e.target.checked)}
                                                />
                                                <span className="form-check-sign"/>
                                                I would like to receive email updates and communications
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row className="mt-4">
                                    <Col md={12} className="text-center">
                                        <Button
                                            size="lg"
                                            onClick={handleSubmit}
                                            disabled={submitting}
                                            style={{
                                                backgroundColor: '#1d8cf8',
                                                borderColor: '#1d8cf8',
                                                color: 'white',
                                                padding: '12px 40px',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {submitting ? "Submitting..." : "Submit Waiver"}
                                        </Button>
                                        <Button
                                            color="secondary"
                                            size="lg"
                                            onClick={() => history.push("/admin/signwaiver")}
                                            className="ml-2"
                                            style={{
                                                padding: '12px 40px'
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default SubmitWaiver;
